/*!
 * Local storage plugin
 * Provides utilities that manages both first party and cross domain local storage.
 */

import './crossdomainstorage';
import 'cookies';
import { getStoragePlugin, ALLOWED_PLUGINS, DEPLOYMENT_PREFIXED_KEYS } from 'util';
import { verifyOrigin } from 'verifyorigin';

Genesys('registerPlugin', 'LocalStorage', (LocalStorage) => {
  /**
     * COMMANDS
     * --------
     * set
     * get
     * remove
     */

  /**
     * EVENTS
     * ------
     * localStorageChanged
     * expired
     */


  /** variables declaration */

  let storageClient = '';
  let bCrossDomainStorage = false; // configurable in Messenger config
  let bInitialized = false;

  const DEFAULT_PREFIX = '_';
  const sStoragePlugin = getStoragePlugin();
  const { deploymentId, preview } = Genesys.c || {};
  const bClearOnStorageChange = true; // Internal flag to control wiping storage data when ever session persistance config option is changed.
  const aStorageKeys = ['gcmcopn', 'gcmcsessionActive', 'gcmksid', 'actsc', 'actec', 'actmr', 'actmu', 'actms', 'actts', 'actvc', 'gcatkn', 'gcartkn', 'gcagttype']; // need list of all keys here to clear them when switching between storage type in the config. We are doing this just to be safe.

  /** Local functions */

  function getStorageKey(key) {
    const sDeploymentPrefix = DEPLOYMENT_PREFIXED_KEYS.includes(key) ? `${deploymentId}:` : '';

    return `${DEFAULT_PREFIX}${sDeploymentPrefix}${key}`;
  }

  /**
   * Only used for normal localStorage
   * @param {object} lsData
   * @param {string} name
   * @param {promise} resolve
   */
  function parseStorageData(lsData, name, resolve) {
    let oLSData = {};

    if (lsData && typeof lsData === 'string') {
      try {
        oLSData = JSON.parse(lsData);
      } catch (e) {
        LocalStorage.log('Error parsing local storage data', e);
      }

      const { value, ttl } = oLSData;

      if (ttl && Date.now() > ttl) { // check for expiry, clear if expired and return empty.
        removeLocalStorage(name).then(()=>{
          LocalStorage.publish('expired', { name });

          resolve();
        });
      } else {
        resolve(value); // resolve with the stored value
      }
    } else {
      resolve(); // No data available, resolve empty
    }
  }

  /**
     * Local method to get the stored value of a localstorage property
     * @param {*} name
     * resolve with value if available, otherwise empty
     */
  function getLocalStorageValue(name) {
    const promiseState = new Promise((resolve, reject) => {
      const sKey = getStorageKey(name);

      if (bCrossDomainStorage) {
        const hostTLD = LocalStorage.data('Cookies.domain');

        storageClient.retrieve({
          key: sKey,
          crossDomainOrigin: hostTLD,
        }).then((lsData)=>{
          resolve(lsData);
        }, () => {
          resolve();
        });
      } else {
        if(window.localStorage){  // eslint-disable-line

          parseStorageData(localStorage.getItem(sKey), name, resolve);  // eslint-disable-line

        } else {
          reject('Your browser does not support localStorage');
        }
      }
    });

    return promiseState;
  }

  function setLocalStorage(name, encodedValue, { expireInSecs }) {
    const setStoragePromise = new Promise((resolve, reject) => {
      let expireAt = null;

      const hostTLD = LocalStorage.data('Cookies.domain');
      const hubUrl = LocalStorage.data('ServiceDiscovery.uris.staticAssets') || location.origin;
      const sKey = getStorageKey(name);

      if (expireInSecs) { // If expiry is available, calculate the expiry time
        expireAt = Date.now() + expireInSecs * 1000; // expires in milli seconds
      }

      if (bCrossDomainStorage) {
        storageClient.store({
          accessors: [hubUrl, hostTLD],
          key: sKey,
          value: encodedValue,
          ttl: expireInSecs * 1000, // need to send just in milliseconds. The crossdomainstorage.js library automatically appends to Date.now()
        }).then((data) => {
          resolve(data);
        });
      } else {
        // first party localStorage
        if (window.localStorage) {  // eslint-disable-line
          localStorage.setItem(sKey, JSON.stringify({ // eslint-disable-line
            value: encodedValue,
            ttl: expireAt,
          }));

          resolve();
        } else {
          reject('Your browser does not support localStorage');
        }
      }
    });

    return setStoragePromise;
  }

  function removeLocalStorage(name) {
    const removeStoragePromise = new Promise((resolve, reject) => {
      const sKey = getStorageKey(name);

      if (bCrossDomainStorage) {
        const hostTLD = LocalStorage.data('Cookies.domain');

        storageClient.remove({
          key: sKey,
          crossDomainOrigin: hostTLD,
        }).then((data)=>{
          resolve(data);
        }, () => {
          resolve();
        });
      } else {
        if (window.localStorage) {
          localStorage.removeItem(sKey);  // eslint-disable-line
          resolve();
        } else {
          reject('Your browser does not support localStorage');
        }
      }
    });

    return removeStoragePromise;
  }

  /**
   * A function that detects storage is changed and clears any previous storage type data
   */
  function clearOnStorageChange(hubUrl) {
    if (bCrossDomainStorage) {
      // remove any existing normal localStorage items
      aStorageKeys.forEach((key) => {
        const sKey = `${DEFAULT_PREFIX}${deploymentId}:${key}`;

        localStorage.removeItem(sKey);
      });
    } else {
      // remove any cdls storage items
      setupCrossDomainStorage(hubUrl).then(() => {
        const hostTLD = LocalStorage.data('Cookies.domain');

        aStorageKeys.forEach((key) => {
          const sKey = `${DEFAULT_PREFIX}${deploymentId}:${key}`;

          storageClient.remove({
            key: sKey,
            crossDomainOrigin: hostTLD,
          }).then(()=>{
          }, () => {
          });
        });
      });
    }
  }

  /** Setup cdls hub url where all storage will be saved */
  function setupCrossDomainStorage(hubUrl) {
    const promiseSetup = new Promise((resolve) => {
      storageClient = window.setupStorageClient({
        targetUrl: `${hubUrl}/genesys-bootstrap/hub.html`,
      });

      storageClient.init().then(() => {
        resolve(); // setup is done
      }, () => {
      });
    });

    return promiseSetup;
  }

  /** Register Commands */

  /*
    * Command: get
    * @description Retrieve a localStorage value by name.
    * @param {String} name - the localStorage property name
    * @param {Array} names (optional) - to fetch multiple storage items at a time
    */
  LocalStorage.registerCommand('get', (e)=>{
    const { name, names } = e.data;

    if (e && e.commander && ALLOWED_PLUGINS.indexOf(e.commander) === -1) {
      return e.reject(`${e.commander} plugin does not have permission to access LocalStorage plugin`);
    }

    if (!name && !names) {
      return e.reject(new Error('invalid parameters'));
    }

    if (!bInitialized) return e.reject('Please wait until LocalStorage plugin is initialized.');

    if (names && Array.isArray(names)) { // to fetch multiple storage items
      const oStorageItems = {};

      names.forEach((item, index) => {
        getLocalStorageValue(item).then((val) => {
          // if no val, it simply resolves empty
          // These are not random inputs from user. For any security related scanning, this can be safely ignored.
          oStorageItems[item] = decodeURIComponent(val); // eslint-disable-line

          if (index === names.length-1) {
            return e.resolve(oStorageItems); // resolve object containing multiple storage keys with its values
          }
        });
      });
    } else {
      getLocalStorageValue(name).then((value)=>{
        if (value) {
          return e.resolve(decodeURIComponent(value));
        } else {
          return e.resolve(`${name} not found`);
        }
      }).catch(()=>{
        return e.resolve(`${name} not found`);
      });
    }
  });


  /*
    * Command: set
    * @description Set a localstorage value.
    * @param {String} name - the localstorage property name
    * @param {String} value - the value of the localstorage property to set
    * @param {Number} [expireInSecs] - a relative time in seconds from now for when the localstorage should expire.
    *                                  If not specified, defaults to 36 hours (TODO: Check with product)
    */
  LocalStorage.registerCommand('set', (e) => {
    const {
      name,
      value,
      expireInSecs,
    } = e.data;

    if (e && e.commander && ALLOWED_PLUGINS.indexOf(e.commander) === -1) {
      return e.reject(`${e.commander} plugin does not have permission to access LocalStorage plugin`);
    }

    if (!name || !value) {
      e.reject(new Error('invalid parameters'));
    }

    if (!bInitialized) return e.reject('Please wait until LocalStorage plugin is initialized.');

    // sanitize the value by URI encoding it
    const encodedValue = encodeURIComponent(value);

    let oldValue = '';

    getLocalStorageValue(name).then((sValue) => {
      if (sValue) {
        oldValue = sValue;

        setLocalStorage(name, encodedValue, { expireInSecs }).then(() => {
          let newValue = '';

          getLocalStorageValue(name).then((sNewValue) => {
            newValue = sNewValue;

            if (newValue !== oldValue) {
              LocalStorage.publish('localStorageChanged', { // updated existing local storage value
                name,
                oldValue,
                newValue,
              });
            }

            return e.resolve(newValue);
          }).catch(() => {});
        }).catch(() => {
          e.reject(`could not set localstorage ${name}`);
        });
      } else {
        // set first time

        setLocalStorage(name, encodedValue, { expireInSecs }).then(()=>{
          LocalStorage.publish('localStorageSet', { name, encodedValue }); // set for the first time

          return e.resolve(encodedValue);
        });
      }
    });
  });


  /*
    * Command: remove
    * @description Deletes a localstorage by the property name.
    * @param {String} name - the localstorage property name
    */
  LocalStorage.registerCommand('remove', (e) => {
    const {
      name,
    } = e.data;

    if (e && e.commander && ALLOWED_PLUGINS.indexOf(e.commander) === -1) {
      return e.reject(`${e.commander} plugin doesn't have permission to access LocalStorage plugin`);
    }

    if (!name) {
      return e.reject('invalid options');
    }

    if (!bInitialized) return e.reject('Please wait until LocalStorage plugin is initialized.');

    getLocalStorageValue(name).then((value)=>{
      if (value) {
        const oldValue = value;

        removeLocalStorage(name).then(()=>{
          // publish event
          LocalStorage.publish('localStorageChanged', {
            name,
            oldValue,
            newValue: null,
          });

          LocalStorage.publish('localStorageRemoved', { name });

          e.resolve();
        }).catch(() => {
          e.reject(`could not remove ${name} from the localstorage`);
        });
      } else {
        return e.resolve(); // resolve to keep it similar to current cookies implementation
      }
    });
  });

  /**
   * Command: configure - internal only
   * @description Configure LocalStorage plugin based on the deployment config
   * @param {Object} config - deployment configuration
   */
  LocalStorage.registerCommand('configure', (e) => {
    const { deploymentConfig } = e.data || {};
    const { messenger } = deploymentConfig || {};
    const { sessionPersistenceType } = messenger || {};

    if (typeof sessionPersistenceType == 'string' && sessionPersistenceType === 'AcrossSubdomains') bCrossDomainStorage = true;

    bInitialized = true;
    e.resolve();
  });


  /** subscriptions */

  /** init */
  /**
   * Set the localStorage type based on the config.
   * - "AcrossSubdomains": storage data is shared across all sub-domains (cdls)
   * - "DomainOrSubdomainOnly": Browser default localStorage behavior
   */
  LocalStorage.subscribe('ServiceDiscovery.ready', () => {
    const hubUrl = LocalStorage.data('ServiceDiscovery.uris.staticAssets') || location.origin;

    // If preview mode, publish ready event directly, no need to check for config.json
    if (preview) {
      bInitialized = true;
      LocalStorage.ready();
    } else {
      LocalStorage.subscribe('GenesysJS.configurationReceived', (e) => {
        const { deploymentConfig } = e.data || {};
        const { messenger } = deploymentConfig || {};
        const { sessionPersistenceType } = messenger || {};
        const bOurOwnOrigin = verifyOrigin(hubUrl);

        if (typeof sessionPersistenceType == 'string' && sessionPersistenceType === 'AcrossSubdomains') bCrossDomainStorage = true;

        bInitialized = true;

        /* On storage change is detected by simply checking for any existing storage keys that are saved in different storage type.
          If any, we will simply remove them. Note that it cannot be removed on our own GC origin because both localStorage and cdls would refer to same type.
        */
        if (bClearOnStorageChange && !bOurOwnOrigin) clearOnStorageChange(hubUrl);

        if (bCrossDomainStorage) {
          setupCrossDomainStorage(hubUrl).then(() => {
            /**
              * Publish ready event only if LocalStorage plugin is enabled, otherwise don't publish.
              * This is to avoid a race-condition where Identifiers can be created twice because all the plugins like Journey are still not ready with LocalStorage.
              * Keeping this way, will also allow switching between Cookies or LocalStorage plugin.
              */

            if (sStoragePlugin === 'LocalStorage') LocalStorage.ready();
          });
        } else {
          // Today's default browser Local Storage behavior
          if (sStoragePlugin === 'LocalStorage') LocalStorage.ready();
        }
      });
    }
  });
});
